/* eslint-disable no-console */
import React, { ReactNode, useState, useEffect, BaseSyntheticEvent } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Link, Text } from '@chakra-ui/react';
import { useUserCache } from 'src/apollo/cache';
import ReCAPTCHA from 'react-google-recaptcha';
import { getUtmCookies } from 'src/utils/getUtmCookies';
import SupportLink from 'src/components/intercom/SupportLink';
import { usePermissionsContext } from 'src/components/permissions/PermissionsContext';
import {
  Tier,
  useCreateCustomerMutation,
  useCreateSubscriptionFromSetupIntentMutation,
} from 'src/generated/graphql';
import useGetUrlParam from 'src/hooks/useGetUrlParam';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import assert from 'src/utils/assert';
import { FormValues } from '../form/FormValues';
import useCreateUser from './useCreateUser';
import useCreateSubscription from './useCreateSubscription';
import trackAffiliate from '../helpers/trackAffilliate';
import { Discount } from '../types';

declare const BASE_URL: string;

export default function useSubmitSignupForm() {
  const navigate = useNavigate();
  const user = useUserCache();
  const createUser = useCreateUser();
  const [createCustomer] = useCreateCustomerMutation();
  const createSubscription = useCreateSubscription();
  const [submissionError, setSubmissionError] = useState<ReactNode | string | null>(null);
  const utmCookies = getUtmCookies();

  const [createSubscriptionFromSetupIntent] = useCreateSubscriptionFromSetupIntentMutation();

  const stripe = useStripe();
  const elements = useElements();

  const { permissions, refetchPermissions } = usePermissionsContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Handle redirects from external payment confirmation pages.
  const redirectStatus = useGetUrlParam('redirect_status');
  useEffect(() => {
    if (!redirectStatus) return;
    (async () => {
      setHasSubmitted(true);
      if (redirectStatus === 'succeeded') {
        navigate(`/signup/processing${window.location.search}`);
      }

      handleSubmissionError();
    })();
  }, [navigate, redirectStatus]);

  // If the user has successfully signed up, take them to the personalise page.
  // Otherwise if they have an account with Alitu already, take them to the homepage.
  useEffect(() => {
    if (isSubmitting) return;
    if (permissions?.tier === Tier.Unlimited || permissions?.tier === Tier.Trial) {
      if (hasSubmitted) {
        navigate('/personalise');
      } else {
        navigate('/');
      }
    }
  }, [permissions, navigate, isSubmitting, hasSubmitted]);

  const createUserIfNeeded = (
    { firstName, lastName, email, emailOptIn, password }: FormValues,
    token: string,
  ) => {
    return user
      ? Promise.resolve()
      : createUser({
          name: `${firstName} ${lastName}`,
          email,
          password,
          emailOptIn,
          token,
          marketingData: utmCookies,
        });
  };

  const createCustomerFromForm = ({
    country,
    address,
    postalCode,
    company,
    promoCode,
    showVatFields,
    vatNumber,
    discount,
    plan,
    paymentMethod,
  }: FormValues) => {
    return createCustomer({
      variables: {
        company: { vatNumber: showVatFields ? vatNumber : '', name: company },
        promoCode: !discount.percentOff && !discount.amountOff ? '' : promoCode, // if both percentageOff and amountOff are zero, do not pass any promo code
        address: {
          line1: address.line1,
          line2: address.line1,
          city: address.city,
          postal_code: postalCode ?? address.postal_code,
          country,
        },
        plan,
        paymentMethod,
      },
    }).then(result => {
      assert(result.data?.createCustomer, 'createCustomer mutation did not return a customer');
      return result.data.createCustomer;
    });
  };

  const createSubscriptionWithoutTrialFromForm = ({
    plan,
    isEuropeanUnion,
    country,
    address,
    postalCode,
    company,
    showVatFields,
    vatNumber,
    promoCode,
    discount,
  }: FormValues) => {
    return createSubscription({
      paymentMethod: '',
      plan,
      isEuropeanUnion,
      company: { vatNumber: showVatFields ? vatNumber : '', name: company },
      withTrial: false,
      promoCode: !discount.percentOff && !discount.amountOff ? '' : promoCode, // if both percentageOff and amountOff are zero, do not pass any promo code
      address: {
        line1: address.line1,
        line2: address.line1,
        city: address.city,
        postal_code: postalCode ?? address.postal_code,
        country,
      },
    });
  };

  const handleSubmissionError = (error?: Error) => {
    setIsSubmitting(false);
    if (error?.message.includes('ERR_ALREADY_SIGNED_UP')) {
      setSubmissionError(
        <Box>
          An account with that email address already exists. Please try{' '}
          <Link asChild>
            <RouterLink to="/login">logging in</RouterLink>
          </Link>
          instead.
        </Box>,
      );
    } else if (error?.message.includes('Cannot create Stripe customer or subscription')) {
      const scrubbedMessage = error.message
        .replace('Cannot create Stripe customer or subscription:', '')
        .replace('Error: Cannot find or create Stripe customer:', '')
        .replace('Error:', '');
      setSubmissionError(
        <Text color="podcastRed600">
          We had a problem verifying your card: <em>{scrubbedMessage}</em> Please check your card
          details, try a different card, or <SupportLink>contact support</SupportLink>.
        </Text>,
      );
    } else {
      setSubmissionError(
        <Text color="podcastRed600">
          An error occurred while registering your account:{' '}
          <em>{error?.message ?? (error as unknown as string) ?? ''}</em>{' '}
          <SupportLink>Contact support</SupportLink>.
        </Text>,
      );
    }
  };

  /**  Trigger form validation and wallet collection */
  const validateStripeElements = async () => {
    if (!stripe || !elements) {
      throw new Error('Stripe is not initialised');
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      // return handleSubmissionError(submitError.message);
      throw new Error(submitError.message);
    }
  };

  const validateRecaptcha = async (recaptchaRef: React.RefObject<ReCAPTCHA>) => {
    if (process.env.NODE_ENV === 'development') {
      return 'TOKEN_NOT_NEEDED';
    }
    /* Workaround for Cypress tests
     * It's bad practice to modify code for tests, but this is the only way Tom could figure to "mock out" the Recaptcha.
     */
    if ((window as any).Cypress) {
      return 'TOKEN_NOT_NEEDED';
    }
    // End of Cypress workaround
    if (recaptchaRef.current === null) {
      throw new Error('bot verification failed');
    }
    recaptchaRef.current.reset();
    const token = await recaptchaRef.current.executeAsync();
    if (token === null) {
      throw new Error('bot verification token is null');
    }
    return token;
  };

  const isFullyDiscounted = (discount: Discount) => {
    return discount.percentOff === 100 && discount.duration === 'forever';
  };

  const processSetupIntent = async (values: FormValues, clientSecret: string) => {
    assert(elements, 'Stripe Elements not initialised');
    assert(clientSecret, 'Client secret not returned from createCustomer mutation');

    /* Workaround for Cypress tests
     * It's bad practice to modify code for tests, but this is the only way Tom could figure to "mock out" Stripe.
     */
    if ((window as any).Cypress) {
      const stripeOutcome = sessionStorage.getItem('cypress-stripe-outcome');
      if (stripeOutcome === 'fail') throw new Error('Stripe test card failed');
      return Promise.resolve();
    }
    // End of Cypress workaround
    let confirmSetupResult;
    if (values.paymentMethod === 'paypal') {
      confirmSetupResult = await stripe?.confirmPayPalSetup(clientSecret, {
        // Return to signup page to continue signup or show errors.
        return_url: `${BASE_URL}signup`,
      });
    }

    confirmSetupResult = await stripe?.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        // Return URL where the customer should be redirected after the SetupIntent is confirmed.
        return_url: `${BASE_URL}signup/processing`,
        payment_method_data: {
          billing_details: {
            address: {
              country: values.country,
              postal_code: values.postalCode,
            },
          },
        },
      },
      redirect: 'if_required',
    });
    if (confirmSetupResult?.error) {
      throw new Error(confirmSetupResult?.error.message);
    }
  };

  const onSubmit = async (
    values: FormValues,
    _: BaseSyntheticEvent | undefined,
    recaptchaRef: React.RefObject<ReCAPTCHA>,
  ) => {
    setSubmissionError(null);
    try {
      console.warn('validating stripe elements');
      await validateStripeElements();
      setHasSubmitted(true);
      setIsSubmitting(true);

      console.warn('validating recaptcha');
      const token = await validateRecaptcha(recaptchaRef);

      console.warn('creating user');
      await createUserIfNeeded(values, token);

      console.warn('created user');
      const { clientSecret, setupIntentId } = await createCustomerFromForm(values);
      if (isFullyDiscounted(values.discount)) {
        // For 100% discount, create a subscription skipping the trial
        await createSubscriptionWithoutTrialFromForm(values);
      } else {
        await processSetupIntent(values, clientSecret);
        await createSubscriptionFromSetupIntent({
          variables: {
            setupIntentId,
          },
        });
      }
      // hide the alitu waves switching banner for new users
      localStorage.setItem('try-alitu-waves-2', 'true');
      trackAffiliate(values.email);
      refetchPermissions();
      setIsSubmitting(false);
    } catch (reason) {
      console.warn('got error with reason', reason);
      handleSubmissionError(reason as Error);
    }
  };

  return { submissionError, onSubmit, isSubmitting, hasSubmitted };
}
